import React from "react";

import { LogItem, validate, Validator, Value } from "lib/build/schema";

import { useValidatorOptions } from "src/features/forms/hooks/useValidatorOptions";
import { FormState } from "src/features/forms/types";

export function useFormState({
	validator,
	initialValue = {}
}: {
	validator: Validator;
	initialValue?: Value;
}): FormState {
	const argsRef = React.useRef({ validator, initialValue });

	const [{ isInitialized, value, messages, warnings, errors }, setState] =
		React.useState<{
			isInitialized: boolean;
			value: Value;
			messages: LogItem[];
			warnings: LogItem[];
			errors: LogItem[];
		}>({
			isInitialized: false,
			value: {},
			messages: [],
			warnings: [],
			errors: []
		});

	const stateRef = React.useRef({ value });
	React.useEffect(() => {
		stateRef.current.value = value;
	}, [value]);

	const options = useValidatorOptions();
	const setScalarValue = React.useCallback(
		async (value: Value, signal?: AbortSignal): Promise<void> => {
			const result = await validate({
				validator: argsRef.current.validator,
				value,
				options
			});
			if (signal?.aborted) {
				return;
			}
			setState({
				isInitialized: true,
				messages: [],
				warnings: [],
				errors: [],
				...result
			});
		},
		[options]
	);

	React.useEffect(() => {
		const controller = new AbortController();
		setScalarValue(argsRef.current.initialValue, controller.signal);
		return () => {
			controller.abort();
		};
	}, [setScalarValue]);

	const setValue = React.useCallback(
		(value: Value | ((prevValue: Value) => Value)): Promise<void> =>
			setScalarValue(
				typeof value === "function"
					? value(stateRef.current.value)
					: value
			),
		[setScalarValue]
	);

	return { isInitialized, value, setValue, messages, warnings, errors };
}
